import { formatDate } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { DocumenttypeDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/documenttype-display-model';
import { DocumentDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/document-display-model';
import { StateService } from '@/core/services/state.service';
import { GlobalEnumsAceRechtsform } from '@/api/models/ace/xFound/Portal/Core/Entities/global-enums-ace-rechtsform';
import { I18nService } from '@/core/services/i18nService';
import { GlobalEnumsAceDokumentenart, LocalizedTextDisplayModel, UserDisplayModel } from '@/api/model-index';
import { AttachmentsValidator } from '@/core/validators/attachments.validator';
import { AnfrageService } from '@/api/service-index';
import { PortalContextService } from '@/core/services/portal-context.service';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';

@Component({
  selector: 'attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss'],
  providers : [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AttachmentsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AttachmentsComponent),
      multi: true
    }
  ]
})
export class AttachmentsComponent implements ControlValueAccessor {
  protected _documentTypes: DocumenttypeDisplayModel[];
  protected anfrageId: string;
  protected _rechtsform: GlobalEnumsAceRechtsform | null;
  protected onChange: any = () => {};
  protected onTouched: any = () => {};
  protected subscriptions: Subscription[] = [];
  protected userModel: UserDisplayModel;

  @Input()
  public get documentTypes(): DocumenttypeDisplayModel[] {
    return this._documentTypes;
  }
  public set documentTypes(documentTypes: DocumenttypeDisplayModel[]) {
    this._documentTypes = documentTypes;
  }

  @Input()
  public set AnfrageId(anfrageId: string) {
    this.anfrageId = anfrageId;
  }

  public get AnfrageId(): string {
    return this.anfrageId;
  }

  @Input()
  public set rechtsform(rechtsform: GlobalEnumsAceRechtsform | null){
    this._rechtsform = rechtsform;
  }

  public get rechtsform(): GlobalEnumsAceRechtsform | null {
    return this._rechtsform;
  }

  public getDocumentTypeName(model: DocumenttypeDisplayModel){
    let title: LocalizedTextDisplayModel | undefined = this.languageservice.getCurrentLocalizedTextDisplayModel(model.name);
    return title?.text ?? "";
  }

  public getDocumentTypeInfoText(model: DocumenttypeDisplayModel){
    let infotext: LocalizedTextDisplayModel | undefined =this.languageservice.getCurrentLocalizedTextDisplayModel(model.infotext);
    return infotext?.text?? "";
  }

  public get visibleDocumentTypes(): DocumenttypeDisplayModel[] {
    return this._documentTypes.filter(type => this.isDocumentViewVisible(type));
  }

  public isUploadRequired(documentType: DocumenttypeDisplayModel) {
    return (documentType.rechtsformen == null || documentType.rechtsformen.length < 1 || (this._rechtsform != null && documentType.rechtsformen.includes(this._rechtsform))) && documentType.isUploadRequired;
  }

  public isRequired(documentType: DocumenttypeDisplayModel) {
    return this.stateService.IsEndvalidationEnabled && this.isUploadRequired(documentType);
  }

  public isDocumentViewVisible(documentType: DocumenttypeDisplayModel){
    return (documentType.rechtsformen == null || documentType.rechtsformen.length < 1 || (this._rechtsform != null && documentType.rechtsformen.includes(this._rechtsform)))
  }

  public isGemeinnuetzigkeitsBescheidInfotextVisible(documentType: DocumenttypeDisplayModel) {
    return documentType.dokumentenart == GlobalEnumsAceDokumentenart.Gemeinnutzigkeitsnachweis && this.userModel.institution
      && this.userModel.institution.gemeinnuetzigkeit && this.userModel.institution.gueltigkeitGemeinnuetzigkeitsbescheid;
  }

  public get gemeinnuetzigkeitsBescheidInfotext(): string {
    const timestamp = new Date(Date.parse(this.userModel.institution?.gueltigkeitGemeinnuetzigkeitsbescheid ?? ""));

    if(timestamp.getTime() < new Date().getTime())
      return this.languageservice.instant("attachments.gemeinnuetzigkeitsbescheid.invalid").replace("{{timestamp}}", formatDate(timestamp, this.languageservice.GetCurrentShortDateFormat(), this.languageservice.language));
    else
      return this.languageservice.instant("attachments.gemeinnuetzigkeitsbescheid.valid").replace("{{timestamp}}", formatDate(timestamp, this.languageservice.GetCurrentShortDateFormat(), this.languageservice.language));
  }

  public attachmentForm: UntypedFormControl;

  public get value(): DocumentDisplayModel[] {
    return this.attachmentForm.value;
  }

  public set value(value: DocumentDisplayModel[]) {
    this.attachmentForm.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  constructor(protected route: ActivatedRoute, 
    protected stateService: StateService,
    protected languageservice: I18nService,
    protected anfrageService: AnfrageService,
    protected portalContextService: PortalContextService,
    protected loadingAnimationService: LoadingAnimationService) { 
    this.initForm();
    this.userModel = this.route.snapshot.data.userModel;

    this.subscriptions.push(
      this.attachmentForm.valueChanges.subscribe(value => {
        this.onChange(value);
        this.onTouched();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.attachmentForm.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onDocumentDelete(index: any): void {
    this.value.splice(index, 1);
    if (this.stateService.IsEndvalidationEnabled)
      this.attachmentForm.updateValueAndValidity();
  }

  onDocumentUpload(document: DocumentDisplayModel): void {
    this.value.push(document);
    if (this.stateService.IsEndvalidationEnabled)
      this.attachmentForm.updateValueAndValidity();
    if(document.documententyp?.dokumentenart == GlobalEnumsAceDokumentenart.Finanzierungsplan){
      
      let subscription = this.anfrageService.apiAnfrageAnfrageIdFinanceDataGet({ anfrageId: this.AnfrageId }).subscribe(response => {
        if (response) {
          this.route.snapshot.data.financeData = response;
  
          this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.AnfrageId, reportId: this.portalContextService.CurrentBericht?.projektberichtId }).subscribe(result => {
            this.stateService.ValidationErrors = result;
          })
  
          if (this.portalContextService.CurrentAnfrage)
              this.portalContextService.CurrentAnfrage.financeData = response;
        }
      });
      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
  }

  setDisabledState(isDisabled: boolean) {
    if(isDisabled)
      this.attachmentForm.disable();
    else
      this.attachmentForm.enable();
  }

  protected initForm(): void {
    this.attachmentForm = new UntypedFormControl([], []);
  }

  validate(_: UntypedFormControl) {
  }

  validateAttatchment(documentType: any) {
    if (this.stateService.IsEndvalidationEnabled) {
      let attachmentsValidator = new AttachmentsValidator(documentType, this.route.snapshot.data.contactData.institution?.rechtsform);
      return this.isUploadRequired(documentType) && attachmentsValidator.validate(this.attachmentForm)!=null;
    }
    return false;
  }
}
